import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { createHttp, type EndpointRequestConfig } from './http';
import { createPlantApi } from './requests/plant';
import { createPvApi } from './requests/pv';

export interface CreateHevitonApiWebLegacyOptions {
  baseURL: string;
  timeoutMs?: number;
  /**
   * access token 게터 — `Authorization: Bearer` 주입. 본 패키지는 데이터 도메인 전용이라
   * 토큰 발급·refresh 는 다루지 않는다 (auth 는 app-legacy 가 소유, 단일 토큰 공유).
   */
  getAccessToken: () => string | null;
  /**
   * access 재발급 함수 — **composition 이 소유하는 공통 single-flight**. 401 시 인터셉터가 호출해 새
   * access token 을 받고 재시도한다. **null = 재발급 불가(원 요청 실패)** 일 뿐이고, 세션을 끝낼지는
   * 실패 사유를 아는 composition 이 이 함수 안에서 스스로 판단한다. 본 패키지엔 auth 엔드포인트가 없어
   * 재발급 소유는 composition(app-legacy refresh) — 세 API 가 같은 함수를 공유한다.
   */
  refreshToken: () => Promise<string | null>;
  /**
   * 세션 확정 만료 통보 — **재발급된 access 마저 401 로 거부된 경우에만** 호출된다.
   * 재발급 실패 자체는 composition 이 사유를 보고 처리하므로 여기로 오지 않는다.
   */
  onAuthError: () => void;
  onMaintenance?: () => void;
  onNetworkError?: () => void;
  onServerError?: (status: number) => void;
}

/**
 * 2.0 공통 응답 래퍼. Source: commons/dto/response/{BaseResponse,ResponseResult}.java.
 * 성공 = `result.code === 0`(HTTP status 아님, 비즈니스 코드). `data` 는 @JsonInclude(NON_NULL)
 * 이라 없을 수 있음. v2/v3 는 bare DTO 라 래퍼 없음 — 2.0 고유.
 */
interface BaseResponse {
  result: { code: number; message?: string };
  data?: unknown;
}

function isBaseResponse(body: unknown): body is BaseResponse {
  return typeof body === 'object' && body !== null && 'result' in body;
}

function buildClient(opts: CreateHevitonApiWebLegacyOptions): AxiosInstance {
  const client = axios.create({
    baseURL: opts.baseURL,
    timeout: opts.timeoutMs ?? 10_000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Authorization 헤더 — 판단 근거는 **엔드포인트가 선언한 `requiresAuth`** 하나뿐(`endpoints.ts`,
  // `http.ts` 가 config 에 실어 보냄). true 면 store access 를 무조건 덮어쓴다(app-legacy 와 단일 토큰
  // 공유) — 401 재시도도 이 경로로 새 토큰을 받는다. false 면 헤더에 손대지 않는다.
  client.interceptors.request.use((config) => {
    const { endpoint } = config as EndpointRequestConfig;
    if (!endpoint?.requiresAuth) return config;
    const access = opts.getAccessToken();
    if (access) config.headers.set('Authorization', `Bearer ${access}`);
    return config;
  });

  client.interceptors.response.use(
    (res) => {
      // 2.0 응답 계약: 성공 = BaseResponse(`{result:{code},data}`) + `result.code === 0` → inner `data` 평탄화.
      // 비-성공은 transport 에서 reject 해 소비처 onError(`parseApiError`)로 표면화 — 레거시 use-async-state
      // `isSuccessful = status===200 && code===0` 미러(with-auth 팩토리와 동일). (2.0 전 엔드포인트가 봉투
      // 사용 → 봉투 아닌 body 는 전역 ExceptionAdvice 의 ProblemDetail 뿐. v2/v3 는 bare DTO 라 이 팩토리 무관.)
      //   · code≠0 (업무 에러): result.message 를 ProblemDetail 모양으로 정규화해 reject.
      //   · 봉투 아님: ProblemDetail(application/problem+json)이 HTTP 200 으로 온 경우 → body 그대로 reject.
      if (isBaseResponse(res.data)) {
        const { code, message } = res.data.result;
        if (code === 0) {
          res.data = res.data.data;
          return res;
        }
        res.data = { status: res.status, detail: message ?? '', properties: { code } };
        return Promise.reject(
          new axios.AxiosError(
            message ?? 'business error',
            'EBUSINESS',
            res.config,
            res.request,
            res,
          ),
        );
      }
      const problem = res.data as { detail?: unknown } | null;
      const detail = typeof problem?.detail === 'string' ? problem.detail : '';
      return Promise.reject(
        new axios.AxiosError(detail || 'request failed', 'EPROBLEM', res.config, res.request, res),
      );
    },
    async (error) => {
      if (axios.isCancel(error)) return Promise.reject(error);

      if (!error.response) {
        opts.onNetworkError?.();
        return Promise.reject(error);
      }

      const { status } = error.response;
      const config = error.config as EndpointRequestConfig | undefined;

      // 401 + `retryAfterTokenRefresh` endpoint → 공통 refreshToken(composition single-flight) 후 1회 재시도.
      //
      // ⚠️ 재발급 실패(null)에 **`onAuthError` 를 부르지 않는다** — 세션 종료 판정은 `refreshToken` 소유자
      // (composition)의 몫이다. 이유 둘: (1) 여기선 실패 **사유**를 모른다(401=세션만료 vs 5xx·타임아웃=판정불가).
      // (2) `refreshToken` 은 single-flight 라 1회 도는데 대기 중이던 요청 N개가 각자 부르면 세션 종료가 N번
      // 통보된다. composition 이 사유를 보고 1회만 종료하고, 여기선 원 요청을 실패시키기만 한다.
      if (status === 401 && config?.endpoint?.retryAfterTokenRefresh && !config._retried) {
        config._retried = true;
        const newAccess = await opts.refreshToken();
        if (!newAccess) return Promise.reject(error);
        return client(config); // request interceptor 가 새 access 자동 부여
      }
      // 재시도 후 재-401 = **방금 재발급받은 access 마저 거부** → 세션 확정 만료 → 강제 로그아웃.
      // 재발급이 성공으로 끝나 composition 이 볼 수 없는 사실이라, `onAuthError` 가 남는 유일한 자리다.
      if (status === 401 && config?._retried) {
        opts.onAuthError();
        return Promise.reject(error);
      }

      if (status === 503) {
        opts.onMaintenance?.();
        return Promise.reject(error);
      }
      if (status >= 500) {
        opts.onServerError?.(status);
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export function createHevitonApiWebLegacy(opts: CreateHevitonApiWebLegacyOptions) {
  // request 함수들은 raw client 가 아니라 `http`(Endpoint 래퍼)를 받는다 — 경로 해석 + 정책 전달 일원화.
  const http = createHttp(buildClient(opts));
  return {
    plant: createPlantApi(http),
    pv: createPvApi(http),
  };
}

export type HevitonApiWebLegacy = ReturnType<typeof createHevitonApiWebLegacy>;
